import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Spin, Button, Space, message } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import FieldItem from './FieldItem';
import { RECIPIENT_COLORS } from '../DocumentWorkflow';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const DMSPDFViewer = ({
  documentData,
  fields = [],
  handleDrop,
  onFieldUpdate,
  recipients = [],
  activeRecipient,
  onScaleChange,
  editable = false,
  movable = false,
  onFieldRemove,
  showDelete = false,
  onApplySignature,
  activeFieldId,
  currentRecipientId,
  allowFieldSelect = false,
}) => {
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [scaleFactor, setScaleFactor] = useState(1);
  const [, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(null);

  // Get PDF URL from document data
  const getPdfUrl = () => {
    if (!documentData) return null;
    if (documentData.type === 'URL' || documentData.type === 'S3_PATH') {
      return documentData.data;
    }
    if (typeof documentData.data === 'string' && documentData.data.startsWith('http')) {
      return documentData.data;
    }
    return documentData.data;
  };

  // Calculate scale factor - react-pdf renders at scale={zoomLevel},
  // so 1 PDF point = zoomLevel CSS pixels
  useEffect(() => {
    setScaleFactor(zoomLevel);
    if (onScaleChange) onScaleChange(zoomLevel);
  }, [zoomLevel, onScaleChange]);

  // PDF load handlers
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
    setPdfError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setPdfLoading(false);
    setPdfError(error);
    message.error('Failed to load PDF document');
  };

  // Get recipient color by ID
  const getRecipientColor = useCallback((recipientId) => {
    const index = recipients.findIndex(
      (r) => r.id === recipientId || r.key === recipientId
    );
    if (index >= 0 && recipients[index].color) {
      return recipients[index].color;
    }
    return RECIPIENT_COLORS[Math.max(0, index) % RECIPIENT_COLORS.length];
  }, [recipients]);

  // Get recipient initials
  const getRecipientInitials = useCallback((recipientId) => {
    const recipient = recipients.find(
      (r) => r.id === recipientId || r.key === recipientId
    );
    if (!recipient) return '?';
    const names = recipient.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return names[0].substring(0, 2).toUpperCase();
  }, [recipients]);

  // Handle click on PDF page to add field
  const handlePageClick = (e, pageNumber) => {
    if (!editable || !activeRecipient) return;




    // Check if this is a drop event or a click event
    // If there's data transfer, it's a drop
    if (e.dataTransfer && e.dataTransfer.getData('fieldType')) {
      return; // Handle in drop event instead
    }
  };

  // Handle drag over for drop zone
  const handleDragOver = (e) => {
    if (!editable) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // Handle drop on PDF page
  const handleDropOnPage = (e, pageNumber) => {
    if (!editable || !handleDrop) return;
    e.preventDefault();

    const fieldType = e.dataTransfer.getData('fieldType');
    const recipientId = e.dataTransfer.getData('recipientId');

    if (!fieldType || !recipientId) {
      console.warn('Missing field type or recipient ID');
      return;
    }

    const pageElement = e.currentTarget;
    const rect = pageElement.getBoundingClientRect();

    // Calculate drop position relative to page
    const dropX = e.clientX - rect.left;
    const dropY = e.clientY - rect.top;

    // Convert to PDF coordinates
    const x = dropX / scaleFactor;
    const y = dropY / scaleFactor;

    handleDrop({
      fieldType,
      recipientId,
      x,
      y,
      pageNumber,
    });

    message.success(`${fieldType} field added`);
  };

  // Handle field click
  const handleFieldClick = (field) => {
    if (onApplySignature) {
      onApplySignature(field.id);
    }
  };

  // Zoom controls
  const handleZoomIn = () => setZoomLevel((prev) => Math.min(2, prev + 0.25));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(0.5, prev - 0.25));

  const pdfUrl = getPdfUrl();

  return (
    <div
      ref={containerRef}
      style={{
        padding: 20,
        height: '100%',
        overflow: 'auto',
      }}
    >
      {/* Zoom Controls */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: '#e8e8e8',
          padding: '8px 0',
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Space>
          <Button
            icon={<ZoomOutOutlined />}
            onClick={handleZoomOut}
            disabled={zoomLevel <= 0.5}
          >
            Zoom Out
          </Button>
          <span style={{ padding: '0 8px' }}>
            {Math.round(zoomLevel * 100)}%
          </span>
          <Button
            icon={<ZoomInOutlined />}
            onClick={handleZoomIn}
            disabled={zoomLevel >= 2}
          >
            Zoom In
          </Button>
        </Space>
      </div>

      {/* PDF Document */}
      {pdfError ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
          <p>Failed to load PDF</p>
          <Button type="primary" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      ) : pdfUrl ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" tip="Loading PDF..." />
              </div>
            }
          >
          {numPages > 0 &&
            [...Array(numPages).keys()].map((_, index) => {
              const pageNumber = index + 1;
              const pageFields = fields.filter((f) => f.page === pageNumber);

              return (
                <div
                  key={pageNumber}
                  style={{
                    position: 'relative',
                    marginBottom: 16,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    background: '#fff',
                    width: 'fit-content',
                  }}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDropOnPage(e, pageNumber)}
                  onClick={(e) => handlePageClick(e, pageNumber)}
                >
                  <Page
                    pageNumber={pageNumber}
                    scale={zoomLevel}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />

                  {/* Field overlays */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      pointerEvents: 'none',
                    }}
                  >
                    {pageFields.map((field) => (
                      <FieldItem
                        key={field.id}
                        field={field}
                        scaleFactor={scaleFactor}
                        onUpdate={onFieldUpdate}
                        onRemove={onFieldRemove}
                        onClick={() => handleFieldClick(field)}
                        isActive={activeFieldId === field.id}
                        isEditable={
                          movable &&
                          (activeRecipient === field.recipient_id ||
                            activeRecipient === field.recipient_id)
                        }
                        showDelete={showDelete}
                        color={getRecipientColor(field.recipient_id)}
                        initials={getRecipientInitials(field.recipient_id)}
                        currentRecipientId={currentRecipientId}
                        allowFieldSelect={allowFieldSelect}
                      />
                    ))}
                  </div>

                  {/* Page number */}
                  <div
                    style={{
                      textAlign: 'center',
                      background: '#f0f0f0',
                      padding: '4px 0',
                      fontSize: 12,
                      color: '#666',
                    }}
                  >
                    Page {pageNumber} of {numPages}
                    {pageFields.length > 0 && ` (${pageFields.length} field${pageFields.length > 1 ? 's' : ''})`}
                  </div>
                </div>
              );
            })}
          </Document>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
          No PDF document available
        </div>
      )}
    </div>
  );
};

export default DMSPDFViewer;
